import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Row, Typography, message, Tag } from 'antd';
import {
  AppstoreOutlined,
  ShopOutlined,
  TeamOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import MainLayout from '../layout/layout';
import { useModulos } from '../../context/modulosContext';
import { apiClient } from '../../config/apiClient';

const { Title, Paragraph } = Typography;

const HomeAdministrador = () => {
  const navigate = useNavigate();
  const isMobile = window.innerWidth <= 768;
  const { modulosActivos, modulosLoaded } = useModulos();
  const [empresa, setEmpresa] = useState(null);

  useEffect(() => {
    const cargarEmpresa = async () => {
      try {
        const response = await apiClient.get('/public/portada');
        if (!response.ok) return;
        const data = await response.json();
        setEmpresa(data?.empresa || null);
      } catch (error) {
        // vista no bloqueante: si falla seguimos con layout sin tarjeta de club
      }
    };
    cargarEmpresa();
  }, []);

  const accesos = useMemo(() => [
    {
      key: 'gestion',
      titulo: 'Gestion',
      descripcion: 'Compras, ventas, caja, cobros y configuraciones del negocio.',
      icono: <AppstoreOutlined style={{ fontSize: isMobile ? 28 : 32, color: '#4f8cff' }} />,
      color: '#3f7dff',
      chips: ['Caja', 'Ventas', 'Cobros', 'Configuracion'],
      onClick: () => {
        if (!modulosLoaded) {
          message.info('Cargando configuración de módulos...');
          return;
        }
        if (!modulosActivos.gestion) {
          message.warning('El módulo gestion está deshabilitado.');
          return;
        }
        localStorage.setItem('moduloSeleccionado', 'gestion');
        navigate('/gestion/dashboard');
      },
    },
    {
      key: 'club',
      titulo: 'Club',
      descripcion: 'Canchas, turnos, jugadores, difusiones y precios.',
      icono: <ShopOutlined style={{ fontSize: isMobile ? 28 : 32, color: '#c3e633' }} />,
      color: '#c3e633',
      chips: ['Canchas', 'Turnos', 'Jugadores', 'Precios', 'Difusiones'],
      onClick: () => {
        if (!modulosLoaded) {
          message.info('Cargando configuración de módulos...');
          return;
        }
        if (!modulosActivos.club) {
          message.warning('El módulo club está deshabilitado.');
          return;
        }
        localStorage.setItem('moduloSeleccionado', 'club');
        navigate('/club/dashboard');
      },
    },
    {
      key: 'academia',
      titulo: 'Academia',
      descripcion: 'Agenda, alumnos, pagos, profesores y configuración.',
      icono: <TeamOutlined style={{ fontSize: isMobile ? 28 : 32, color: '#ff9f43' }} />,
      color: '#ff9f43',
      chips: ['Agenda', 'Alumnos', 'Profesores', 'Pagos'],
      onClick: () => {
        if (!modulosLoaded) {
          message.info('Cargando configuración de módulos...');
          return;
        }
        if (!modulosActivos.academia) {
          message.warning('El módulo academia está deshabilitado.');
          return;
        }
        localStorage.setItem('moduloSeleccionado', 'academia');
        navigate('/dashboard');
      },
    },
  ], [isMobile, modulosActivos, modulosLoaded, navigate]);

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? 10 : 28, maxWidth: 1280 }}>
        {empresa?.nombre && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'linear-gradient(180deg, rgba(30,37,50,0.5) 0%, rgba(20,22,28,0.7) 100%)',
              marginBottom: 22,
            }}
          >
            {empresa.foto_base64 ? (
              <img
                src={`data:${empresa.foto_tipo || 'image/jpeg'};base64,${empresa.foto_base64}`}
                alt=""
                style={{ width: 34, height: 34, borderRadius: 10, objectFit: 'cover' }}
              />
            ) : (
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(216,255,62,0.2)' }} />
            )}
            <div style={{ lineHeight: 1.2 }}>
            <div style={{ color: '#eef2ff', fontWeight: 700, fontSize: 14 }}>{empresa.nombre}</div>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                {empresa.localidad_nombre || empresa.direccion || 'Club'}
              </div>
            </div>
          </div>
        )}

        <Paragraph style={{ color: '#8d94a5', marginBottom: 24, fontSize: isMobile ? 13 : 15 }}>
          Accesos directos a los módulos principales.
        </Paragraph>

        <Row
          gutter={[16, 16]}
          justify="center"
          style={{ maxWidth: 1120, margin: '0 auto' }}
        >
          {accesos.map((acceso) => (
            <Col key={acceso.key} xs={24} sm={12} lg={8}>
              <Card
                hoverable
                onClick={acceso.onClick}
                onMouseEnter={(e) => {
                  if (modulosLoaded && modulosActivos[acceso.key]) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 18px 34px rgba(0,0,0,0.35)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                style={{
                  background: 'linear-gradient(180deg, #232323 0%, #1d1d1d 100%)',
                  borderColor: 'rgba(255,255,255,0.08)',
                  borderTop: `2px solid ${acceso.color}`,
                  cursor: modulosLoaded && modulosActivos[acceso.key] ? 'pointer' : 'not-allowed',
                  height: '100%',
                  filter: modulosLoaded && modulosActivos[acceso.key] ? 'none' : 'blur(1.8px)',
                  opacity: modulosLoaded && modulosActivos[acceso.key] ? 1 : 0.55,
                  borderRadius: 18,
                  transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease',
                }}
                styles={{
                  body: {
                    minHeight: isMobile ? 260 : 320,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: isMobile ? '22px 18px' : '28px 24px',
                  },
                }}
              >
                <div
                  style={{
                    width: isMobile ? 64 : 72,
                    height: isMobile ? 64 : 72,
                    borderRadius: 18,
                  display: 'flex',
                  alignItems: 'center',
                    justifyContent: 'center',
                    background: `${acceso.color}22`,
                    marginBottom: 16,
                }}
              >
                  {acceso.icono}
                </div>
                <Title level={4} style={{ color: '#f5f8ff', margin: 0, fontSize: isMobile ? 24 : 30, fontWeight: 800, lineHeight: 1 }}>
                  {acceso.titulo}
                </Title>
                <Paragraph style={{ color: '#8e96a8', marginTop: 8, marginBottom: 16, fontSize: isMobile ? 12 : 14, lineHeight: 1.35 }}>
                  {acceso.descripcion}
                </Paragraph>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {acceso.chips.map((chip) => (
                    <Tag
                      key={chip}
                      style={{
                        marginInlineEnd: 0,
                        borderRadius: 999,
                        paddingInline: 10,
                        border: '1px solid rgba(255,255,255,0.08)',
                        color: '#7f8799',
                        background: 'rgba(255,255,255,0.03)',
                        fontSize: isMobile ? 10 : 11,
                      }}
                    >
                      {chip}
                    </Tag>
                  ))}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </MainLayout>
  );
};

export default HomeAdministrador;
